import { useCallback, useEffect, useRef, useState } from 'react';
import WebButton from '../../../../component/library/webButton';
import { SellingModes } from '../../../../helper/constants';
import { useBatchIntersectionObserver } from '../../../../hooks/useIntersectionObserver';
import { getPriceForQuantity, getTotalSelectedQuantity, enrichVariantPhysicalsFromProductShipping, appendSizeToImageUrl } from './utils';
import { ToastTypes, useMessage } from '../../../../base/context/MessageProvider';

const VariantList = ({
  productUnit,
  isDbProduct,
  variantDetails,
  groupedVariants,
  sortedAttributeGroups,
  selectedAttrValues,
  setSelectedAttrValues,
  onVariantSelect,
  onQuantityChange,
  pricingMode,
  selectedVariants,
  priceRanges,
  isWholesale,
  variantPrices = {},
  onVariantVisible,
  setTotalWholesalePrice,
  isMinMaxRange,
  MOQ,
  customMOQ,
  product = {}, // Add product prop for non-variant products
  initialVariantId = null, // Add initialVariantId for auto-scroll
  askForPricingQuotation = false,
}) => {
  const [hoveredImage, setHoveredImage] = useState(null);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });
  const [showCopyMessage, setShowCopyMessage] = useState(false);
  const [editingValues, setEditingValues] = useState({}); // Track which inputs are being edited
  const elementRefsRef = useRef(new Map());
  const initialLoadTriggeredRef = useRef(false);
  const prevVariantKeyRef = useRef(''); // Track previous variant selection to detect changes
  const colorScrollContainerRef = useRef(null); // Ref for color scroll container
  const colorItemRefsRef = useRef(new Map()); // Refs for individual color items
  const variantRowRefsRef = useRef(new Map()); // Refs for variant rows (for auto-scroll)
  const variantListContainerRef = useRef(null); // Ref for variant list container
  const message = useMessage();
  const [zoomModal, setZoomModal] = useState({ show: false, image: '' });
  const [showFullProductName, setShowFullProductName] = useState(false);

  // Batch size for quantity steps (Alibaba products can have batchNumber)
  // Apply batch logic ONLY in wholesale mode; dropshipping stays step = 1
  const rawBatchNumber = Number(product?.batchNumber) || 0;
  const batchSize = isWholesale && rawBatchNumber > 1 ? rawBatchNumber : 1;

  // Variants without weight/dimensions: hide quantity +/- (show Send inquiry only). Uses productShippingInfo / globalApiResponse when SKU row is incomplete.
  const parseNum = v => (v === undefined || v === null ? 0 : (typeof v === 'string' ? parseFloat(v) : Number(v)) || 0);
  const variantHasWeightAndDimensions = v => {
    if (!v) return false;
    const e = enrichVariantPhysicalsFromProductShipping(product, v.weight, v.length, v.height, v.width);
    const w = parseNum(e.rawWeight);
    const h = parseNum(e.rawHeight);
    const l = parseNum(e.rawLength);
    const wd = parseNum(e.rawWidth);
    return w > 0 && h > 0 && l > 0 && wd > 0;
  };

  const handleVariantImageClick = imageUrl => {
    if (!imageUrl) return;
    setZoomModal({ show: true, image: imageUrl });
  };
  const handleVariantModalClose = () => {
    setZoomModal({ show: false, image: '' });
  };
  // Intersection observer for lazy loading variant prices
  const handleVariantVisible = useCallback(
    skuId => {
      if (onVariantVisible) {
        onVariantVisible(skuId);
      }
    },
    [onVariantVisible]
  );

  const { observe, unobserve } = useBatchIntersectionObserver(handleVariantVisible);

  // Cleanup refs when component unmounts
  useEffect(() => {
    return () => {
      elementRefsRef.current.clear();
    };
  }, []);

  // Trigger price calculation for initially visible variants on mount
  useEffect(() => {
    if (!isDbProduct && !isWholesale && sortedAttributeGroups?.length > 0 && variantDetails && onVariantVisible && !initialLoadTriggeredRef.current) {
      initialLoadTriggeredRef.current = true;

      // Trigger calculation for first 10 variants to get initial min price
      const skuIds = Object.keys(variantDetails).slice(0, 10);

      // Stagger the calls slightly to avoid API overload
      skuIds.forEach((skuId, index) => {
        setTimeout(() => onVariantVisible(skuId), index * 50);
      });
    }
  }, [pricingMode, sortedAttributeGroups, variantDetails, onVariantVisible]);

  // Reset initial load flag when mode changes
  useEffect(() => {
    initialLoadTriggeredRef.current = false;
  }, [pricingMode]);

  // Reset showFullProductName when product changes
  useEffect(() => {
    setShowFullProductName(false);
  }, [product?.title, product?.name]);

  // Clear editing values when variant selection changes to ensure correct quantity display
  // This ensures that when switching between variants (e.g., Dark skin tone to Ivory white),
  // the quantity input shows the correct quantity for the newly selected variant, not the previous one
  useEffect(() => {
    // Create a key from selectedAttrValues to detect actual variant changes
    // Only clear editing values when the variant combination actually changes
    const variantKey = sortedAttributeGroups
      ?.map(grp => selectedAttrValues[grp.id] || '')
      .join('|') || '';

    // Only clear editing values if the variant selection actually changed (not on initial mount)
    if (prevVariantKeyRef.current !== variantKey && prevVariantKeyRef.current !== '') {
      // Variant selection changed, clear editing values to force re-render with correct quantity
      setEditingValues({});
    }
    prevVariantKeyRef.current = variantKey;
  }, [selectedAttrValues, sortedAttributeGroups]);

  // Auto-scroll to selected color and variant row when initialVariantId is provided
  useEffect(() => {
    if (initialVariantId && initialVariantId !== '0' && variantDetails && Object.keys(variantDetails).length > 0 && sortedAttributeGroups?.length > 0) {
      // Find the variant by initialVariantId
      let variant = variantDetails[initialVariantId];
      if (!variant) {
        variant = Object.values(variantDetails).find(v => String(v.skuId) === String(initialVariantId));
      }

      if (variant && variant.skuAttributes) {
        // Find the color attribute group (first one, usually isColor)
        const colorAttrGroup = sortedAttributeGroups[0];
        if (colorAttrGroup) {
          // Find the color value for this variant
          const colorAttr = variant.skuAttributes.find(attr => String(attr.attributeId) === String(colorAttrGroup.id));
          if (colorAttr) {
            const colorValue = colorAttr.valueTrans || colorAttr.value;
            // Scroll to the color item after a short delay to ensure DOM is ready
            setTimeout(() => {
              const colorItemEl = colorItemRefsRef.current.get(colorValue);
              if (colorItemEl && colorScrollContainerRef.current) {
                colorItemEl.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
              }
            }, 100);
          }
        }

        // Also scroll to the variant row in the list (for the last attribute group with quantity controls)
        const skuId = variant.skuId;
        setTimeout(() => {
          const variantRowEl = variantRowRefsRef.current.get(String(skuId));
          if (variantRowEl) {
            variantRowEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
            // Add a brief highlight effect
            variantRowEl.style.transition = 'background-color 0.3s ease';
            variantRowEl.style.backgroundColor = '#e0f7fa';
            setTimeout(() => {
              variantRowEl.style.backgroundColor = '';
            }, 1500);
          }
        }, 300); // Slightly longer delay to ensure color scroll happens first
      }
    }
  }, [initialVariantId, variantDetails, sortedAttributeGroups]);

  const handleAttributeValueClick = (attrId, valueKey, variants) => {
    // Clear editing values when user clicks on a different variant option
    // This ensures the quantity input shows the correct value for the newly selected variant
    setEditingValues({});

    setSelectedAttrValues(prev => ({
      ...prev,
      [attrId]: valueKey,
    }));

    // Find matching variant based on all selected attributes
    const selectedAttrs = { ...selectedAttrValues, [attrId]: valueKey };
    const matchingVariant = Object.values(variantDetails).find(variant => {
      return Object.keys(selectedAttrs).every(selectedAttrId => {
        const attr = variant.skuAttributes?.find(a => String(a.attributeId) === String(selectedAttrId));
        return attr && (attr.valueTrans === selectedAttrs[selectedAttrId] || attr.value === selectedAttrs[selectedAttrId]);
      });
    });

    if (matchingVariant && onVariantSelect) {
      onVariantSelect({
        color: valueKey,
        spec: valueKey,
        variantId: matchingVariant.skuId,
        variant: matchingVariant,
      });
    }
  };

  const handleQuantityChange = (skuId, newQuantity, variant) => {
    // Check productSaleInfo.amountOnSale first
    const isOutOfStock = product?.productSaleInfo?.amountOnSale === 0;
    if (isOutOfStock) {
      message.showToast('Out of stock', ToastTypes.WARNING);
      return;
    }

    const availableStock = Number(variant.amountOnSale) || 0;
    if (availableStock <= 0 && newQuantity > 0) {
      message.showToast('Out of stock for this variant', ToastTypes.WARNING);
      return;
    }

    if (newQuantity > availableStock) {
      message.showToast(`Max available stock is ${availableStock}.`, ToastTypes.WARNING);
      onQuantityChange(skuId, availableStock, variant);
      return;
    }

    onQuantityChange(skuId, newQuantity, variant);
  };

  const incrementQuantity = (skuId, variant) => {
    // Check productSaleInfo.amountOnSale first
    const isOutOfStock = product?.productSaleInfo?.amountOnSale === 0;
    if (isOutOfStock) {
      message.showToast('Out of stock', ToastTypes.WARNING);
      return;
    }
    const currentQty = selectedVariants[skuId]?.quantity || 0;
    const availableStock = Number(variant.amountOnSale) || 0;

    // In dropshipping mode, always increment by 1
    const step = isWholesale ? batchSize : 1;
    const newQty = currentQty + step;
    // If no stock or trying to go beyond stock, show toast
    if (availableStock <= 0 || newQty > availableStock) {
      message.showToast('Out of stock for this variant', ToastTypes.WARNING);
      return;
    }
    // Attach calculatedFBP to variant for downstream price calculation if needed
    const updatedVariant = { ...variant, finalPrice: variant.calculatedFBP ?? variant.finalPrice };
    handleQuantityChange(skuId, newQty, updatedVariant);
  };

  const decrementQuantity = (skuId, variant) => {
    // Check productSaleInfo.amountOnSale first
    const isOutOfStock = product?.productSaleInfo?.amountOnSale === 0;
    if (isOutOfStock) {
      message.showToast('Out of stock', ToastTypes.WARNING);
      return;
    }
    const currentQty = selectedVariants[skuId]?.quantity || 0;
    if (currentQty <= 0) return;

    // In dropshipping mode, always decrement by 1
    const step = isWholesale ? batchSize : 1;
    const newQty = currentQty - step;
    // Attach calculatedFBP to variant for downstream price calculation if needed
    const updatedVariant = { ...variant, finalPrice: variant.calculatedFBP ?? variant.finalPrice };
    handleQuantityChange(skuId, newQty > 0 ? newQty : 0, updatedVariant);
  };

  const handleQuantityInputChange = (skuId, value, variant) => {
    setEditingValues(prev => ({ ...prev, [skuId]: value }));
    if (value === '') return;
    const numValue = parseInt(value);
    if (!isNaN(numValue) && numValue >= 0 && numValue <= variant.amountOnSale) {
      // Attach calculatedFBP to variant for downstream price calculation (same as +/- buttons)
      const updatedVariant = { ...variant, finalPrice: variant.calculatedFBP ?? variant.finalPrice };
      handleQuantityChange(skuId, numValue, updatedVariant);
    }
  };

  const handleQuantityInputBlur = (skuId, value, variant) => {
    setEditingValues(prev => {
      const { [skuId]: _, ...rest } = prev;
      return rest;
    });

    if (value === '' || isNaN(parseInt(value))) {
      handleQuantityChange(skuId, 0, variant);
      return;
    }

    let numValue = parseInt(value);

    // Enforce batch multiples only for wholesale mode
    if (isWholesale && batchSize > 1 && !isNaN(numValue) && numValue > 0) {
      const remainder = numValue % batchSize;
      if (remainder !== 0) {
        numValue = numValue - remainder + batchSize;
        message.showToast(`Quantity adjusted to nearest batch multiple of ${batchSize}.`, ToastTypes.INFO);
      }
    }

    // Attach calculatedFBP to variant for downstream price calculation (same as +/- buttons)
    const updatedVariant = { ...variant, finalPrice: variant.calculatedFBP ?? variant.finalPrice };
    handleQuantityChange(skuId, numValue, updatedVariant);
  };

  const handleImageHover = (imageUrl, e) => {
    if (!imageUrl) return;
    const rect = e.currentTarget.getBoundingClientRect();
    setTooltipPosition({ x: rect.left - 240, y: rect.top });
    setHoveredImage(imageUrl);
  };
  const truncate = txt => (txt?.length > 15 ? txt.slice(0, 14) + '…' : txt);

  const copyToClipboard = async (text, e) => {
    e.stopPropagation();
    if (navigator.clipboard && text) {
      try {
        await navigator.clipboard.writeText(text);
        setShowCopyMessage(true);
        setTimeout(() => setShowCopyMessage(false), 2000);
      } catch (err) {
        console.error('Failed to copy:', err);
      }
    }
  };

  // Calculate total quantity for attribute value
  const getTotalQuantityForAttributeValue = (valueKey, attrGroup) =>
    Object.values(selectedVariants).reduce(
      (total, v) =>
        v.skuAttributes?.some(attr => String(attr.attributeId) === String(attrGroup.id) && (attr.valueTrans === valueKey || attr.value === valueKey))
          ? total + (v.quantity || 0)
          : total,
      0
    );

  const renderAttributeSection = (attrGroup, showQuantityControls, isHorizontal) => {
    const values = Array.from(attrGroup.values.entries());
    const isColorGroup = isHorizontal; // Color groups are rendered horizontally

    return (
      <div key={attrGroup.id} className="mb-4">
        <div className="flex items-baseline gap-2 mb-1.5">
          <h3 className="text-[14px] font-semibold">{attrGroup.nameTrans}</h3>
          {isWholesale && String(attrGroup.nameTrans).toLowerCase() === 'color' && batchSize > 1 && (
            <span className="text-[11px] font-medium text-yellow-600">
              Per variant quantity = {batchSize} units
            </span>
          )}
        </div>
        <div
          ref={isColorGroup ? colorScrollContainerRef : null}
          className={
            isHorizontal
              ? 'flex gap-2 overflow-x-auto overflow-y-hidden pb-2 custom-scrollbar'
              : 'max-h-[300px] overflow-y-auto border border-[#e5e7eb] rounded-md custom-scrollbar'
          }
        >
          {values.map(([valueKey, valueData]) => {
            const isSelected = selectedAttrValues[attrGroup.id] === valueKey;

            // For quantity-controlled group, find the variant that matches ALL currently selected attributes
            let variant = valueData.variants[0];
            let skuId = variant?.skuId;

            if (showQuantityControls && groupedVariants) {
              const combinationKey = (sortedAttributeGroups || [])
                .map((grp, idx) => (idx === sortedAttributeGroups.length - 1 ? valueKey : selectedAttrValues[grp.id] || Array.from(grp.values.keys())[0]))
                .join('');
              const exactVariant = groupedVariants.get(combinationKey);
              if (exactVariant) {
                variant = exactVariant;
                skuId = exactVariant.skuId;
              } else {
                // Fallback: find variant that matches all selected attributes including the current valueKey
                const selectedAttrs = { ...selectedAttrValues, [attrGroup.id]: valueKey };
                const matchingVariant = valueData.variants.find(v => {
                  return Object.keys(selectedAttrs).every(selectedAttrId => {
                    const attr = v.skuAttributes?.find(a => String(a.attributeId) === String(selectedAttrId));
                    return attr && (attr.valueTrans === selectedAttrs[selectedAttrId] || attr.value === selectedAttrs[selectedAttrId]);
                  });
                });
                if (matchingVariant) {
                  variant = matchingVariant;
                  skuId = matchingVariant.skuId;
                }
              }
            }

            // Show editing value if user is currently editing, otherwise show actual quantity
            const currentQty = editingValues.hasOwnProperty(skuId) ? editingValues[skuId] : (selectedVariants[skuId]?.quantity ?? 0);

            // Calculate total quantity for this attribute value (for non-quantity-controlled groups)
            const totalQtyForValue = !showQuantityControls ? getTotalQuantityForAttributeValue(valueKey, attrGroup) : 0;

            // Only calculate range price for wholesale mode + quantity-controlled groups
            // Use original offerPrice as base (before any range discounts)
            // console.log('varaint ', valueData , variant)
            const finalPrice = variant.finalPrice;
            const originalPrice = showQuantityControls
              ? parseFloat(variant.finalPrice || variant?.fenxiaoPriceInfo?.onePiecePrice || variant?.consignPrice || variant?.fenxiaoPriceInfo?.offerPrice || 0)
              : // ? parseFloat(variant.cp || variant.ap)
              0;
            const totalSelectedQty = getTotalSelectedQuantity(selectedVariants);
            const firstRangeStart = priceRanges?.[0]?.startQuantity;
            let rangePrice = isWholesale && showQuantityControls && totalSelectedQty >= firstRangeStart ? getPriceForQuantity(originalPrice, totalSelectedQty, priceRanges) : null;
            rangePrice = rangePrice ? rangePrice.finalPrice : priceRanges?.[0]?.finalPrice;
            rangePrice && setTotalWholesalePrice(totalSelectedQty * rangePrice);
            const strikeThruRangePrice = currentQty > 0 ? priceRanges?.find(r => r.startQuantity < currentQty)?.finalPrice : null;
            // Get calculated dropshipping price for this variant
            const dropshippingPriceData = variantPrices[skuId];
            const dropshippingFinalPrice = dropshippingPriceData?.finalPrice;
            // For database products, use variant's finalPrice as fallback if dropshippingPriceData is not available
            const variantFallbackPrice = isDbProduct && !dropshippingFinalPrice ?
              (parseFloat(variant.finalPrice) || parseFloat(variant.ap) || parseFloat(variant.cp) || parseFloat(variant.consignPrice) || parseFloat(variant.fenxiaoPriceInfo?.onePiecePrice) || parseFloat(variant.price) || 0) : null;
            // Only show calculating if it's not a database product OR if database product has no price at all
            const isPriceCalculating = !isWholesale && !dropshippingPriceData && (!variantFallbackPrice || variantFallbackPrice === 0);
            // Check if product is out of stock
            const isOutOfStock = product?.productSaleInfo?.amountOnSale === 0;

            return (
              <div
                key={valueKey}
                ref={el => {
                  // Store ref for color items (horizontal) for auto-scroll
                  if (isColorGroup) {
                    if (el) {
                      colorItemRefsRef.current.set(valueKey, el);
                    } else {
                      colorItemRefsRef.current.delete(valueKey);
                    }
                  }
                  // Store ref for variant rows (vertical list) for auto-scroll
                  if (showQuantityControls && skuId && variantHasWeightAndDimensions(variant)) {
                    if (el) {
                      variantRowRefsRef.current.set(String(skuId), el);
                    } else {
                      variantRowRefsRef.current.delete(String(skuId));
                    }
                  }
                  // Intersection observer for price loading (only for variants with weight/dimensions)
                  if (showQuantityControls && !isWholesale && variantHasWeightAndDimensions(variant)) {
                    const prevEl = elementRefsRef.current.get(skuId);
                    if (prevEl && prevEl !== el) {
                      unobserve(prevEl);
                    }
                    if (el) {
                      elementRefsRef.current.set(skuId, el);
                      observe(el, skuId);
                    } else {
                      elementRefsRef.current.delete(skuId);
                    }
                  }
                }}
                onClick={() => handleAttributeValueClick(attrGroup.id, valueKey, valueData.variants)}
                className={`cursor-pointer relative ${isHorizontal
                  ? `flex flex-col items-center gap-1 p-2 border rounded-md min-w-[80px] flex-shrink-0 ${isSelected ? 'border-[#3DD0E5] border-2 bg-[#f0f9ff]' : 'border-[#e5e7eb] hover:border-[#3DD0E5]'
                  }`
                  : `grid grid-cols-[1fr_auto] gap-2 p-3 px-2 border-b last:border-b-0 hover:bg-[#f9fafb] items-center ${isSelected ? 'bg-[#f0f9ff] border-l-4 border-l-[#3DD0E5]' : ''}`
                  }`}
              >
                {/* Quantity Badge for non-quantity-controlled attributes */}
                {!showQuantityControls && totalQtyForValue > 0 && (
                  <div className="absolute top-1 right-1 bg-[#3DD0E5] text-white text-[10px] font-semibold px-1.5 py-0.5 rounded-full min-w-[20px] text-center z-1">
                    {totalQtyForValue}
                  </div>
                )}
                <div className={`flex items-center gap-1 ${isHorizontal ? 'flex-col' : ''}`}>
                  {valueData.imageUrl && (
                    <img
                      referrerPolicy="no-referrer"
                      src={appendSizeToImageUrl(valueData.imageUrl, `60x60`)}
                      alt={valueData.valueTrans}
                      // onMouseEnter={e => handleImageHover(valueData.imageUrl, e)}
                      // onMouseLeave={() => setHoveredImage(null)}
                      // onClick={e => {
                      //   e.stopPropagation();
                      //   handleVariantImageClick(valueData.imageUrl);
                      // }}
                      className={`flex-shrink-0 ${isHorizontal ? 'w-[50px] h-[50px] object-cover rounded' : 'w-[40px] h-[40px] object-cover rounded'}`}
                    />
                  )}
                  <div className={`${isHorizontal ? 'text-center max-w-[80px]' : 'flex-1 min-w-0'}`}>
                    <span className="text-[12px] block w-fit font-medium cursor-text" title={valueData.valueTrans}>
                      {valueData.valueTrans}
                    </span>
                    <span className="text-[6px] text-black cursor-text hidden" onClick={e => copyToClipboard(valueData.value, e)} title={valueData.value}>
                      {truncate(valueData.value)}
                    </span>
                    {!isHorizontal && showQuantityControls && (() => {
                      const disp = enrichVariantPhysicalsFromProductShipping(product, variant.weight, variant.length, variant.height, variant.width);
                      return (
                        <div className="text-[10px] text-gray-600 mt-2">
                          Wt : {(disp.rawWeight ?? 0).toFixed(3)}kg | L : {(disp.rawLength ?? 0).toFixed(2)}cm | W : {(disp.rawWidth ?? 0).toFixed(2)}cm | H : {(disp.rawHeight ?? 0).toFixed(2)}cm
                        </div>
                      );
                    })()}
                    {/* Price display for horizontal layout when quantity controls are shown */}
                    {/* {isHorizontal && showQuantityControls && (
                      <div className="text-center mt-1">
                        {isWholesale ? (
                          <>
                            {currentQty > 1 && <div className="text-[10px] text-gray-400">₹{(currentQty * (isMinMaxRange ? originalPrice : rangePrice))?.toFixed(2)}</div>}
                            {isMinMaxRange ? (
                              <div className="text-[10px] text-gray-400">₹{originalPrice?.toFixed(2)} / Piiece</div>
                            ) : currentQty > 1 ? (
                              <>
                                <span className="text-[10px] text-gray-400 line-through">₹{originalPrice?.toFixed(2)}</span>
                                <span className={`text-[12px] font-medium text-green-600`}>₹{rangePrice?.toFixed(2)} / Piiiece</span>
                              </>
                            ) : (
                              <div className={`text-[12px] font-medium text-green-600`}>₹{originalPrice?.toFixed(2)} / Piiii-ece</div>
                            )}
                          </>
                        ) : !isWholesale && dropshippingFinalPrice ? (
                          <div className="text-[12px] font-medium text-blue-600">₹{dropshippingFinalPrice.toFixed(2)}</div>
                        ) : !isWholesale && variantFallbackPrice ? (
                          <div className="text-[12px] font-medium text-blue-600">₹{variantFallbackPrice.toFixed(2)}</div>
                        ) : !isWholesale && !dropshippingPriceData ? (
                          <div className="text-[10px] text-gray-500 italic">Calculating...</div>
                        ) : (
                          <div className="text-[12px] font-medium">
                            ₹ {parseFloat(variant.finalPrice || variant.fenxiaoPriceInfo?.onePiecePrice || variant.consignPrice || variant.fenxiaoPriceInfo?.offerPrice).toFixed(2)}
                          </div>
                        )}
                      </div>
                    )} */}
                  </div>
                </div>
                {!isHorizontal && showQuantityControls && variantHasWeightAndDimensions(variant) && (
                  <div key={`qty-controls-${skuId}`} className="flex flex-col items-center md:flex-row md:items-center gap-2 md:gap-3">
                    <div className="order-2 md:order-1 text-center md:text-right">
                      {!isOutOfStock && (
                        <>
                          {/* Variants with weight/dimensions always show price alongside quantity */}
                          {isWholesale ? (
                            <>
                              {currentQty > 1 && (() => {
                                let grossFBP = Number(variant.calculatedFBP) || Number(finalPrice) || 0;
                                if (Array.isArray(priceRanges) && priceRanges.length > 2 && currentQty > 0) {
                                  const sortedRanges = [...priceRanges].sort((a, b) => a.startQuantity - b.startQuantity);
                                  let activeRange = sortedRanges[0];
                                  for (let i = 0; i < sortedRanges.length; i++) {
                                    if (currentQty >= sortedRanges[i].startQuantity) {
                                      activeRange = sortedRanges[i];
                                    } else {
                                      break;
                                    }
                                  }
                                  if (activeRange && activeRange.calculatedFBP !== undefined) {
                                    grossFBP = Number(activeRange.calculatedFBP);
                                  }
                                }
                                return (
                                  <div className="text-[10px] text-gray-400">
                                    ₹{((currentQty * grossFBP) || 0).toFixed(2)}
                                  </div>
                                );
                              })()}
                              {(() => {
                                // Only apply if priceRanges has more than 1 entry AND user has selected some quantity
                                if (Array.isArray(priceRanges) && priceRanges.length > 1) {
                                  // Use the total selected quantity for all variants
                                  const sortedRanges = [...priceRanges].sort((a, b) => a.startQuantity - b.startQuantity);
                                  const totalQty = getTotalSelectedQuantity(selectedVariants);
                                  // Only show strike-through pricing if user has selected quantity
                                  if (totalQty > 0) {
                                    let activeRange = sortedRanges[0];
                                    // Find the active range based on totalQty
                                    for (let i = sortedRanges.length - 1; i >= 0; i--) {
                                      if (totalQty >= sortedRanges[i].startQuantity) {
                                        activeRange = sortedRanges[i];
                                        break;
                                      }
                                    }
                                    const currentFBP = Number(variant.calculatedFBP) || Number(finalPrice) || 0;
                                    const activeRangeFBP = Number(activeRange.calculatedFBP) || 0;

                                    // Show strike-through when totalQty qualifies for a better price range
                                    // Check if there's a price difference and totalQty meets the range requirement
                                    if (activeRange && activeRangeFBP !== undefined && totalQty >= activeRange.startQuantity && currentFBP > activeRangeFBP) {
                                      return (
                                        <div className="text-[13px] font-medium text-blue-600">
                                          <span className="line-through mr-1">₹{currentFBP.toFixed(2)}</span>
                                          <span>₹{activeRangeFBP.toFixed(2)}</span> / {productUnit || 'piece'}
                                        </div>
                                      );
                                    } else {
                                      // When no discount applies, show the current price
                                      return (
                                        <div className="text-[13px] font-medium text-blue-600">
                                          ₹{currentFBP.toFixed(2)} / {productUnit || 'piece'}
                                        </div>
                                      );
                                    }
                                  } else {
                                    const currentFBP = Number(variant.calculatedFBP) || Number(finalPrice) || 0;
                                    return (
                                      <div className="text-[13px] font-medium text-blue-600">
                                        ₹{currentFBP.toFixed(2)} / {productUnit || 'piece'}
                                      </div>
                                    );
                                  }
                                } else {
                                  const currentFBP = Number(variant.calculatedFBP) || Number(finalPrice) || 0;
                                  return (
                                    <div className="text-[13px] font-medium text-blue-600">
                                      ₹{currentFBP.toFixed(2)} / {productUnit || 'piece'}
                                    </div>
                                  );
                                }
                              })()}
                            </>
                          ) : dropshippingFinalPrice ? (
                            <div className="text-[13px] font-medium text-blue-600">₹{(dropshippingFinalPrice ?? 0).toFixed(2)}</div>
                          ) : variantFallbackPrice ? (
                            <div className="text-[13px] font-medium text-blue-600">₹{(variantFallbackPrice ?? 0).toFixed(2)}</div>
                          ) : !dropshippingPriceData ? (
                            <div className="text-[11px] text-gray-500 italic">Calculating...</div>
                          ) : (
                            <div className="text-[13px] font-medium">
                              ₹{' '}
                              {!isDbProduct && isWholesale
                                ? ((rangePrice !== null ? rangePrice : originalPrice) ?? 0).toFixed(2)
                                : (parseFloat(variant.finalPrice || variant.fenxiaoPriceInfo?.onePiecePrice || variant.consignPrice || variant.fenxiaoPriceInfo?.offerPrice) || 0).toFixed(2)}
                            </div>
                          )}
                        </>
                      )}
                      <div className={`text-[10px] ${isOutOfStock ? 'text-red-500 font-medium' : 'text-gray-500'}`}>
                        {isOutOfStock ? 'Out of stock' : `${variant.amountOnSale || 0} units in stock`}
                      </div>
                    </div>
                    <div className="order-1 md:order-2 flex items-center border border-[#e5e7eb] rounded-md bg-white w-[60px] md:w-auto">
                      <WebButton
                        onClick={e => {
                          e.stopPropagation();
                          decrementQuantity(skuId, variant);
                        }}
                        disabled={isPriceCalculating}
                        className="w-[20px] md:w-[28px] h-[28px] flex items-center justify-center hover:bg-[#f3f4f6] text-[16px] font-semibold text-gray-600 px-1"
                        text="-"
                        type="button"
                      />
                      <input
                        key={`qty-${skuId}`}
                        type="number"
                        value={currentQty}
                        onChange={e => handleQuantityInputChange(skuId, e.target.value, variant)}
                        onBlur={e => handleQuantityInputBlur(skuId, e.target.value, variant)}
                        onClick={e => e.stopPropagation()}
                        disabled={isPriceCalculating}
                        className="w-[30px] md:w-[45px] text-center text-[13px] font-medium border-0 outline-none"
                      />
                      <WebButton
                        onClick={e => {
                          e.stopPropagation();
                          incrementQuantity(skuId, variant);
                        }}
                        disabled={isPriceCalculating}
                        className="w-[20px] md:w-[28px] h-[28px] flex items-center justify-center hover:bg-[#f3f4f6] text-[16px] font-semibold text-gray-600 px-1"
                        text="+"
                        type="button"
                      />
                    </div>
                  </div>
                )}
                {!isHorizontal && showQuantityControls && !variantHasWeightAndDimensions(variant) && (
                  <div key={`qty-controls-${skuId}`} className="flex flex-col items-center md:flex-row md:items-center gap-2 md:gap-3">
                    <div className="order-2 md:order-1 text-center md:text-right">
                      {!isOutOfStock && (
                        <>
                          {isWholesale ? (
                            <>
                              {currentQty > 1 && <div className="text-[10px] text-gray-400">₹{((currentQty * (Number(variant.calculatedFBP) || Number(finalPrice) || 0)) || 0).toFixed(2)}</div>}
                              <div className={`text-[13px] font-medium text-blue-600`}>
                                ₹{(Number(variant.calculatedFBP) || Number(finalPrice) || 0).toFixed(2)} / {productUnit || 'piece'}
                              </div>
                            </>
                          ) : dropshippingFinalPrice ? (
                            <div className="text-[13px] font-medium text-blue-600">₹{(dropshippingFinalPrice ?? 0).toFixed(2)}</div>
                          ) : variantFallbackPrice ? (
                            <div className="text-[13px] font-medium text-blue-600">₹{(variantFallbackPrice ?? 0).toFixed(2)}</div>
                          ) : !dropshippingPriceData ? (
                            <div className="text-[11px] text-gray-500 italic">Calculating...</div>
                          ) : (
                            <div className="text-[13px] font-medium">
                              ₹{' '}
                              {!isDbProduct && isWholesale
                                ? ((rangePrice !== null ? rangePrice : originalPrice) ?? 0).toFixed(2)
                                : (parseFloat(variant.finalPrice || variant.fenxiaoPriceInfo?.onePiecePrice || variant.consignPrice || variant.fenxiaoPriceInfo?.offerPrice) || 0).toFixed(2)}
                            </div>
                          )}
                        </>
                      )}
                      <div className={`text-[10px] ${isOutOfStock ? 'text-red-500 font-medium' : 'text-gray-500'}`}>
                        {isOutOfStock ? 'Out of stock' : `${variant.amountOnSale || 0} units in stock`}
                      </div>
                    </div>
                    <div className="order-1 md:order-2 flex items-center border border-[#e5e7eb] rounded-md bg-white w-[60px] md:w-auto">
                      <WebButton
                        onClick={e => {
                          e.stopPropagation();
                          decrementQuantity(skuId, variant);
                        }}
                        disabled={isPriceCalculating}
                        className="w-[20px] md:w-[28px] h-[28px] flex items-center justify-center hover:bg-[#f3f4f6] text-[16px] font-semibold text-gray-600 px-1"
                        text="-"
                        type="button"
                      />
                      <input
                        key={`qty-${skuId}`}
                        type="number"
                        value={currentQty}
                        onChange={e => handleQuantityInputChange(skuId, e.target.value, variant)}
                        onBlur={e => handleQuantityInputBlur(skuId, e.target.value, variant)}
                        onClick={e => e.stopPropagation()}
                        disabled={isPriceCalculating}
                        className="w-[30px] md:w-[45px] text-center text-[13px] font-medium border-0 outline-none"
                      />
                      <WebButton
                        onClick={e => {
                          e.stopPropagation();
                          incrementQuantity(skuId, variant);
                        }}
                        disabled={isPriceCalculating}
                        className="w-[20px] md:w-[28px] h-[28px] flex items-center justify-center hover:bg-[#f3f4f6] text-[16px] font-semibold text-gray-600 px-1"
                        text="+"
                        type="button"
                      />
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  // Handle products without variants - show quantity selector
  if (!sortedAttributeGroups || sortedAttributeGroups.length === 0) {
    // Price for no-variant: show from priceRanges.finalPrice (main product FBP), then product fallbacks
    const noVariantPriceFromRanges = parseFloat(priceRanges?.[0]?.finalPrice) || 0;
    const noVariantPrice = noVariantPriceFromRanges || parseFloat(product.sp) || parseFloat(product.consign_price) || parseFloat(product.price) || 0;

    // Create a pseudo-variant from product data for quantity management
    // Use String() so key matches productDetails index.js (no sku/offerId type mismatch)
    const noVariantKey = String(product.sku ?? product.offerId ?? product._id ?? 'no-variant');
    const productVariant = {
      skuId: noVariantKey,
      sku: product.sku || product.offerId || product._id,
      offerId: product.offerId || product.sku || product._id,
      amountOnSale: product.amountOnSale || product.quantity || 999999,
      weight: product.weight || 0,
      length: product.length || 0,
      width: product.width || 0,
      height: product.height || 0,
      finalPrice: noVariantPrice,
      consignPrice: noVariantPrice,
      cp: noVariantPrice,
      ap: noVariantPrice,
      fenxiaoPriceInfo: {
        offerPrice: noVariantPrice,
        onePiecePrice: noVariantPrice,
      },
      wholesaleFinalPrice: noVariantPrice,
      price: noVariantPrice,
    };

    const currentQty = editingValues.hasOwnProperty(noVariantKey)
      ? editingValues[noVariantKey]
      : (selectedVariants[noVariantKey]?.quantity ?? 0);

    // Calculate price for display
    const totalSelectedQty = getTotalSelectedQuantity(selectedVariants);
    const firstRangeStart = priceRanges?.[0]?.startQuantity;
    const originalPrice = parseFloat(productVariant.finalPrice || productVariant.consignPrice || productVariant.fenxiaoPriceInfo?.offerPrice || 0);
    let rangePrice = isWholesale && totalSelectedQty >= firstRangeStart
      ? getPriceForQuantity(originalPrice, totalSelectedQty, priceRanges)
      : null;
    rangePrice = rangePrice ? rangePrice.finalPrice : priceRanges?.[0]?.finalPrice;
    rangePrice && setTotalWholesalePrice(totalSelectedQty * rangePrice);

    // Get dropshipping price if available (from variantPrices or main product price)
    const dropshippingPriceData = variantPrices[noVariantKey];
    const dropshippingFinalPrice = dropshippingPriceData?.finalPrice;
    // For products without variants: fall back to main product price so we never show ₹0.00
    const mainProductPrice = parseFloat(priceRanges?.[0]?.finalPrice || product.sp || product.consign_price || 0) || 0;
    const variantFallbackPrice = isDbProduct && !dropshippingFinalPrice
      ? (parseFloat(productVariant.finalPrice) || parseFloat(productVariant.consignPrice) || 0)
      : null;
    const effectiveDropshippingPrice = dropshippingFinalPrice ?? mainProductPrice ?? variantFallbackPrice;
    const isPriceCalculating = !isWholesale && !dropshippingPriceData && !mainProductPrice && (!variantFallbackPrice || variantFallbackPrice === 0);
    // Check if product is out of stock
    const isOutOfStock = product?.productSaleInfo?.amountOnSale === 0;
    const finalPrice = productVariant.finalPrice;

    // Get product image - use imgUrl[0]
    const productImage = product.image || (product.imgUrl && product.imgUrl.length > 0 ? product.imgUrl[0] : null);
    const fullProductName = product.title || product.name || 'Product';

    const noVarDisp = enrichVariantPhysicalsFromProductShipping(
      product,
      productVariant.weight,
      productVariant.length,
      productVariant.height,
      productVariant.width
    );

    // Truncate title to 4-5 words
    const words = fullProductName.split(' ');
    const WORD_LIMIT = 5;
    const isLongName = words.length > WORD_LIMIT;
    const truncatedName = isLongName ? words.slice(0, WORD_LIMIT).join(' ') + '...' : fullProductName;
    const displayName = showFullProductName ? fullProductName : truncatedName;

    return (
      <div className="variant-list-container relative bg-white rounded-lg shadow-sm px-2.5 py-3 md:p-4 border border-gray-100">
        <div
          className="grid grid-cols-[1fr_auto] gap-2 p-3 px-2 border-b last:border-b-0 hover:bg-[#f9fafb] items-center"
        >
          <div className="flex items-center gap-1 flex-1 min-w-0">
            {productImage && (
              <img
                referrerPolicy="no-referrer"
                src={appendSizeToImageUrl(productImage, `60x60`)}
                alt={fullProductName}
                className="flex-shrink-0 w-[40px] h-[40px] object-cover rounded"
              />
            )}
            <div className="flex-1 min-w-0">
              <span className="text-[12px] block w-fit font-medium cursor-text" title={fullProductName}>
                {displayName}
                {isLongName && !showFullProductName && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowFullProductName(true);
                    }}
                    className="ml-1 text-[11px] font-normal text-[#3DD0E5] hover:text-[#24b5cb] inline align-baseline"
                  >
                    See more
                  </button>
                )}
                {isLongName && showFullProductName && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowFullProductName(false);
                    }}
                    className="ml-1 text-[11px] font-normal text-[#3DD0E5] hover:text-[#24b5cb] inline align-baseline"
                  >
                    See less
                  </button>
                )}
              </span>
              <div className="text-[10px] text-gray-600 mt-2">
                Wt : {(noVarDisp.rawWeight ?? 0).toFixed(3)}kg | L : {(noVarDisp.rawLength ?? 0).toFixed(2)}cm | W : {(noVarDisp.rawWidth ?? 0).toFixed(2)}cm | H : {(noVarDisp.rawHeight ?? 0).toFixed(2)}cm
              </div>
            </div>
          </div>
          <div className="flex flex-col items-center md:flex-row md:items-center gap-2 md:gap-3">
            <div className="order-2 md:order-1 text-center md:text-right">
              {!isOutOfStock && (
                <>
                  {askForPricingQuotation ? null : isWholesale ? (
                    <>
                      {currentQty > 1 && (
                        <div className="text-[10px] text-gray-400">
                          ₹{((currentQty * (Number(productVariant.calculatedFBP) || Number(finalPrice) || 0)) || 0).toFixed(2)}
                        </div>
                      )}
                      <div className="text-[13px] font-medium text-blue-600">
                        ₹{(Number(productVariant.calculatedFBP) || Number(finalPrice) || 0).toFixed(2)} / {productUnit || 'piece'}
                      </div>
                    </>
                  ) : !isWholesale && (dropshippingFinalPrice ?? effectiveDropshippingPrice) ? (
                    <div className="text-[13px] font-medium text-blue-600">
                      ₹{(dropshippingFinalPrice ?? effectiveDropshippingPrice ?? 0).toFixed(2)} / {productUnit || 'piece'}
                    </div>
                  ) : !isWholesale && !dropshippingPriceData && !mainProductPrice ? (
                    <div className="text-[11px] text-gray-500 italic">Calculating...</div>
                  ) : (
                    <div className="text-[13px] font-medium">
                      ₹{' '}
                      {!isDbProduct && isWholesale
                        ? ((rangePrice !== null ? rangePrice : originalPrice) ?? 0).toFixed(2)
                        : (parseFloat(productVariant.finalPrice || productVariant.consignPrice || productVariant.fenxiaoPriceInfo?.offerPrice) || effectiveDropshippingPrice || 0).toFixed(2)}
                    </div>
                  )}
                </>
              )}
              <div className={`text-[10px] ${isOutOfStock ? 'text-red-500 font-medium' : 'text-gray-500'}`}>
                {isOutOfStock ? 'Out of stock' : `${productVariant.amountOnSale || 0} units in stock`}
              </div>
            </div>
            <div className="order-1 md:order-2 flex items-center border border-[#e5e7eb] rounded-md bg-white w-[60px] md:w-auto">
              <WebButton
                onClick={e => {
                  e.stopPropagation();
                  decrementQuantity(noVariantKey, productVariant);
                }}
                disabled={isPriceCalculating}
                className="w-[20px] md:w-[28px] h-[28px] flex items-center justify-center hover:bg-[#f3f4f6] text-[16px] font-semibold text-gray-600 px-1"
                text="-"
                type="button"
              />
              <input
                key={`qty-${noVariantKey}`}
                type="number"
                value={currentQty}
                onChange={e => handleQuantityInputChange(noVariantKey, e.target.value, productVariant)}
                onBlur={e => handleQuantityInputBlur(noVariantKey, e.target.value, productVariant)}
                onClick={e => e.stopPropagation()}
                disabled={isPriceCalculating}
                className="w-[30px] md:w-[45px] text-center text-[13px] font-medium border-0 outline-none"
              />
              <WebButton
                onClick={e => {
                  e.stopPropagation();
                  incrementQuantity(noVariantKey, productVariant);
                }}
                disabled={isPriceCalculating}
                className="w-[20px] md:w-[28px] h-[28px] flex items-center justify-center hover:bg-[#f3f4f6] text-[16px] font-semibold text-gray-600 px-1"
                text="+"
                type="button"
              />
            </div>
          </div>
        </div>
      </div>
    );
  }

  const totalGroups = sortedAttributeGroups.length;
  const isSingleSection = totalGroups === 1;

  return (
    <div className="variant-list-container relative bg-white rounded-lg shadow-sm px-2.5 py-3 md:p-4 border border-gray-100">
      {sortedAttributeGroups.map((attrGroup, index) => {
        const isLastSection = index === totalGroups - 1;
        // const isHorizontalLayout = attrGroup.isColor && !(isSingleSection && isLastSection);
        const isHorizontalLayout = attrGroup.isColor && !isLastSection;
        return renderAttributeSection(attrGroup, isLastSection, isHorizontalLayout);
      })}
      {/*
      {hoveredImage && (
        <div className="fixed z-[9] pointer-events-none" style={{ left: `${tooltipPosition.x}px`, top: `${tooltipPosition.y}px` }}>
          <div className="bg-white border-2 rounded-lg shadow-lg p-2">
            <img referrerPolicy="no-referrer" src={hoveredImage} alt="Preview" className="w-[200px] h-[200px] object-cover rounded" />
          </div>
        </div>
      )} */}
      {/* {zoomModal.show && (
        <div className="zoom_modal" onClick={handleVariantModalClose}>
          <div className="zoom_modal_content" onClick={e => e.stopPropagation()}>
            <button className="modal_close_btn" onClick={handleVariantModalClose}>
              ×
            </button>
            <img
              referrerPolicy="no-referrer"
              src={zoomModal.image}
              alt="Variant image"
              className="zoom_modal_image"
            />
          </div>
        </div>
      )} */}

      {showCopyMessage && <div className="fixed bottom-4 right-4 bg-black text-white text-sm px-3 py-2 rounded">Copied to clipboard</div>}
    </div>
  );
};

export default VariantList;
